import type { Clause } from '@gonfalon/clauses/types';
import { deepMerge } from '@gonfalon/collections/deepMerge';
import { ContextKindKey } from '@gonfalon/contexts/types';
import { disabledRules } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { FeatureFlag, FeatureFlagConfig, Rule, Variation } from '@gonfalon/flags/types';
import { OPERATORS as CLAUSE_OPERATORS } from '@gonfalon/instructions/constants';
import type { GuardedReleasePhaseConfig } from '@gonfalon/instructions/instructionTypes';
import { getControlAndTestVariations } from '@gonfalon/instructions/utils';
import { stringifyValue } from '@gonfalon/types/stringifyValue';
import nullthrows from 'nullthrows';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import type { PendingProgressiveReleasePhase } from '#components/automated-rollouts/progressive-rollouts/FlagTargetingPendingProgressiveReleasePhasesProvider';
import { FALLTHROUGH_RULE_ID } from '#components/automated-rollouts/utils';
import type { ConfigVariationIds, PendingGuardedReleasePhase } from '#components/targeting/guarded-rollout/types';
import { idOrKey } from '#utils/flags/idOrKey';

import type {
  ClauseSchema,
  Fallthrough,
  FallthroughSchema,
  FeatureFlagConfigSchema,
  GuardedRolloutSchema,
  IndividualTargetingSchema,
  PercentageRolloutSchema,
  Prerequisite,
  ProgressiveRolloutSchema,
  Rollout,
  RuleSchema,
} from './types';

/** JSON <-> Config **/

// This is where we can feature flag/transform the variation
interface Match<T> {
  id?: T;
}
function parseVariation<T>(
  variations: Variation[],
  match: Match<T> & { required: true },
): { index: number; id: string };
function parseVariation<T>(
  variations: Variation[],
  match: Match<T>,
): { index: number | undefined; id: string | undefined };
function parseVariation<T>(variations: Variation[], match: Match<T> & { required?: true }) {
  const { id, required } = match;
  let varIdx = -1;
  if (typeof id === 'string') {
    varIdx = variations.findIndex((variation) => variation._id === id);
  }
  if (typeof id === 'number') {
    varIdx = id;
  }

  const variation = variations[varIdx];
  if (required) {
    nullthrows(variation, 'Expected a valid variation');
  }

  return {
    index: varIdx === -1 ? undefined : varIdx,
    id: variation ? nullthrows(variation?._id) : undefined,
  };
}

function parseProgressiveRolloutConfig(
  progressiveRolloutConfig: ProgressiveRolloutSchema,
  variations: Variation[],
  controlAndTestVariationIds: ConfigVariationIds,
): PendingProgressiveReleasePhase['configuration'] {
  const { endVariation, controlVariation, steps, contextKind } = progressiveRolloutConfig;
  // for flags with 2 variations, we need to infer end and control variation
  const endVar = endVariation === undefined ? controlAndTestVariationIds.testVariationId : endVariation;
  const controlVar = controlVariation === undefined ? controlAndTestVariationIds.controlVariationId : controlVariation;

  return {
    contextKind,
    endVariationId: parseVariation(variations, {
      id: endVar,
      required: true,
    }).id,
    controlVariationId: parseVariation(variations, {
      id: controlVar,
      required: true,
    }).id,
    steps: steps.map((step) => ({
      ...step,
      key: uuid(),
    })),
  };
}

function parseAndMergeRules(params: {
  originalConfig: FeatureFlagConfig;
  draftRules: FeatureFlagConfigSchema['rules'];
  variations: Variation[];
}): Rule[] {
  const { originalConfig, draftRules, variations } = params;
  const { rules: originalRules } = originalConfig;
  const originalRulesMap = new Map(originalRules?.map((item) => [nullthrows(item._id), item]) ?? []);

  const mergedRules =
    draftRules?.map<Rule>((rule) => {
      const { _id, variation, description, progressiveRolloutConfig, guardedRolloutConfig } = rule;
      const originalRule = typeof _id === 'string' ? originalRulesMap.get(_id) : undefined;
      const rollout = parseRollout(rule.percentageRolloutConfig, variations);

      let ruleVariation = variation;

      // set default variation for rule with rollouts
      if (ruleVariation === undefined && (progressiveRolloutConfig || guardedRolloutConfig)) {
        const { testVariationId } = getControlAndTestVariationIds({
          ruleId: _id ?? '',
          rule,
          originalConfig,
          variations,
        });
        ruleVariation = variations.findIndex((v) => v._id === testVariationId);
      }

      if (originalRule) {
        // You should not be allowed to edit a rule with an experiment allocation
        if (originalRule.rollout?.experimentAllocation) {
          return originalRule;
        }
        const originalClauses = new Map(originalRule.clauses.map((item) => [nullthrows(item._id), item]) ?? []);

        const clauses = rule.clauses.map<Clause>((clause) => {
          const { _id: id } = clause;
          const originalClause = typeof id === 'string' ? originalClauses.get(id) : undefined;
          if (originalClause) {
            return deepMerge<Clause>(originalClause, clause);
          }

          return {
            ...clause,
            _id: '',
          };
        });

        if (rollout) {
          const { variation: _variation, ...originalRuleFields } = originalRule;
          return deepMerge<Rule>(originalRuleFields, {
            rollout,
            description,
            clauses,
          });
        }
        const { rollout: _rollout, ...originalRuleFields } = originalRule;

        return deepMerge<Rule>(originalRuleFields, {
          description,
          variation: parseVariation(variations, {
            id: ruleVariation,
          }).index,
          clauses,
          ...(disabledRules() && { disabled: originalRule.disabled }),
        });
      }
      const key = uuid();
      // new rules should have an empty _id
      return {
        ...(rollout
          ? { rollout }
          : {
              variation: parseVariation(variations, {
                id: ruleVariation,
              }).index,
            }),
        description,
        ref: key,
        _key: key,
        _id: '',
        trackEvents: false,
        ...(disabledRules() && { disabled: rule.disabled }),
        clauses: rule.clauses.map<Clause>((clause) => ({
          ...clause,
          _id: '',
        })),
      };
    }) ?? [];

  // If there are any rules with non-percentage rollouts that are not in the draft rules, add them
  originalRules?.forEach((rule, idx) => {
    if (rule.rollout?.experimentAllocation) {
      const mergedRule = mergedRules.find((r) => idOrKey(r) === idOrKey(rule));
      if (!mergedRule) {
        mergedRules.splice(idx, 0, rule);
      }
    }
  });
  return mergedRules;
}

function parseConfigToPrerequisites(params: {
  draftPrerequisites: FeatureFlagConfigSchema['prerequisites'];
  prerequisiteFlags: Record<string, FeatureFlag>;
}) {
  const { draftPrerequisites, prerequisiteFlags } = params;

  return (
    draftPrerequisites?.map((draftPrerequisite) => {
      const { key, variation: variationId } = draftPrerequisite;
      const variations = nullthrows(prerequisiteFlags[key]?.variations, `Invalid prerequisite flag: ${key}`);
      const { index: variation } = parseVariation(variations, {
        id: variationId,
        required: true,
      });

      return {
        key,
        variation,
      };
    }) ?? []
  );
}

function parseRollout(rollout: PercentageRolloutSchema | undefined, variations: Variation[]): Rollout | undefined {
  if (!rollout) {
    return undefined;
  }
  const { contextKind, bucketBy } = rollout;

  return {
    contextKind,
    bucketBy,
    variations: rollout.variations.map(({ variation: id, weight }) => {
      const { index: variation } = parseVariation(variations, {
        id,
        required: true,
      });

      return {
        weight,
        variation,
      };
    }),
  };
}

function parseAndMergeFallthrough(params: {
  originalFallthrough: FeatureFlagConfig['fallthrough'];
  fallthroughSchema: FallthroughSchema | undefined;
  variations: Variation[];
}): Fallthrough | undefined {
  const { originalFallthrough, fallthroughSchema, variations } = params;
  if (!fallthroughSchema) {
    return originalFallthrough;
  }

  if (originalFallthrough?.rollout?.experimentAllocation) {
    return originalFallthrough;
  }

  const rollout = parseRollout(fallthroughSchema.percentageRolloutConfig, variations);

  if (rollout) {
    return {
      rollout,
    };
  }

  const { index: variation } = parseVariation(variations, {
    id: fallthroughSchema.variation ?? originalFallthrough?.variation,
  });
  return {
    variation,
  };
}

function parseConfigToContextTargets(
  contextTargetsSchema: NonNullable<FeatureFlagConfigSchema['contextTargets']>,
  variations: Variation[],
): Pick<FeatureFlagConfig, 'targets' | 'contextTargets'> {
  const contextTargetConfig = contextTargetsSchema.map(({ variation: variationId, ...contextTarget }) => {
    const { index: variation } = parseVariation(variations, {
      id: variationId,
      required: true,
    });
    return {
      ...contextTarget,
      variation,
    };
  });
  const contextTargets = contextTargetConfig.filter((target) => target.contextKind !== ContextKindKey.USER);
  const targets = contextTargetConfig.filter((target) => target.contextKind === ContextKindKey.USER);

  return {
    targets,
    contextTargets,
  };
}

const defaultNotify = true;

const parseConfigToPendingGuardedRollout = (params: {
  pendingGuardedRollout: GuardedRolloutSchema;
  configurationKey: string;
  variations: Variation[];
  controlAndTestVariationIds: ConfigVariationIds;
}): PendingGuardedReleasePhase => {
  const { pendingGuardedRollout, configurationKey, variations, controlAndTestVariationIds } = params;
  const { randomizationUnit, stages, metrics, metricSources, testVariation, controlVariation } = pendingGuardedRollout;

  // for flags with 2 variations, we need to infer test and control variation
  const testVar = testVariation === undefined ? controlAndTestVariationIds.testVariationId : testVariation;
  const controlVar = controlVariation === undefined ? controlAndTestVariationIds.controlVariationId : controlVariation;

  return {
    testVariationId:
      parseVariation(variations, {
        id: testVar,
        required: true,
      }).id ?? '',
    controlVariationId:
      parseVariation(variations, {
        id: controlVar,
        required: true,
      }).id ?? '',
    configurationKey,
    configuration: {
      randomizationUnit,
      metrics: metrics.map((metric) => {
        const onRegression = { ...metric.onRegression, notify: defaultNotify };
        return { ...metric, onRegression };
      }),
      metricSources,
      stages,
      rolloutWeight: stages.slice(-1)[0]?.rolloutWeight ?? 0,
      monitoringWindowMilliseconds:
        stages.reduce((acc, { monitoringWindowMilliseconds }) => acc + monitoringWindowMilliseconds, 0) ?? 0,
    },
  };
};

const getControlAndTestVariationIds = (params: {
  ruleId: string;
  rule: { variation?: number | undefined };
  originalConfig: FeatureFlagConfig;
  variations: Variation[];
}) => {
  const { ruleId, rule, originalConfig, variations } = params;
  const originalRule = originalConfig.rules?.find((r) => idOrKey(r) === ruleId);
  const { controlVariation, testVariation } = getControlAndTestVariations({
    flagConfig: originalConfig,
    originalVariation: originalRule?.variation,
    draftVariation: rule.variation,
    fallthroughVariation: originalConfig.fallthrough?.variation,
    variations,
  });

  return {
    controlVariationId: controlVariation._id,
    testVariationId: testVariation._id,
  };
};

export function parseJsonToConfig(params: {
  schemaConfig: FeatureFlagConfigSchema;
  draftConfig: FeatureFlagConfig;
  originalConfig: FeatureFlagConfig;
  variations: Variation[];
  prerequisiteFlags: Record<string, FeatureFlag>;
}) {
  const { schemaConfig, variations, draftConfig, originalConfig, prerequisiteFlags } = params;
  const { on, offVariation, contextTargets = [], fallthrough, prerequisites, rules = [] } = schemaConfig;
  const featureFlagConfig = deepMerge<FeatureFlagConfig>(draftConfig, {
    on,
    offVariation: parseVariation(variations, {
      id: offVariation,
    })?.index,
    ...parseConfigToContextTargets(contextTargets, variations),
  });
  featureFlagConfig.fallthrough = parseAndMergeFallthrough({
    originalFallthrough: originalConfig.fallthrough,
    fallthroughSchema: fallthrough,
    variations,
  });
  featureFlagConfig.prerequisites = parseConfigToPrerequisites({
    prerequisiteFlags,
    draftPrerequisites: prerequisites,
  });
  featureFlagConfig.rules = parseAndMergeRules({ originalConfig, draftRules: rules, variations });

  let pendingProgressiveRollout: PendingProgressiveReleasePhase | undefined;
  let indexWithConfig = rules.findIndex(({ progressiveRolloutConfig }) => progressiveRolloutConfig) ?? -1;
  const ruleWithPendingProgressiveRolloutConfig = indexWithConfig > -1 ? rules[indexWithConfig] : undefined;

  if (ruleWithPendingProgressiveRolloutConfig?.progressiveRolloutConfig) {
    const configurationKey = nullthrows(
      ruleWithPendingProgressiveRolloutConfig._id
        ? ruleWithPendingProgressiveRolloutConfig._id
        : featureFlagConfig.rules?.[indexWithConfig].ref,
    );

    const controlAndTestVariationIds = getControlAndTestVariationIds({
      ruleId: configurationKey,
      rule: ruleWithPendingProgressiveRolloutConfig,
      originalConfig,
      variations,
    });

    pendingProgressiveRollout = {
      configurationKey,
      configuration: parseProgressiveRolloutConfig(
        ruleWithPendingProgressiveRolloutConfig.progressiveRolloutConfig,
        variations,
        controlAndTestVariationIds,
      ),
    };
  } else if (schemaConfig.fallthrough?.progressiveRolloutConfig) {
    const controlAndTestVariationIds = getControlAndTestVariationIds({
      ruleId: FALLTHROUGH_RULE_ID,
      rule: schemaConfig.fallthrough,
      originalConfig,
      variations,
    });
    pendingProgressiveRollout = {
      configurationKey: FALLTHROUGH_RULE_ID,
      configuration: parseProgressiveRolloutConfig(
        schemaConfig.fallthrough.progressiveRolloutConfig,
        variations,
        controlAndTestVariationIds,
      ),
    };
  }

  let pendingGuardedRollout: PendingGuardedReleasePhase | undefined;
  indexWithConfig = rules.findIndex(({ guardedRolloutConfig }) => guardedRolloutConfig) ?? -1;
  const ruleWithPendingGuardedRolloutConfig = indexWithConfig > -1 ? rules[indexWithConfig] : undefined;
  if (ruleWithPendingGuardedRolloutConfig?.guardedRolloutConfig) {
    const configurationKey = nullthrows(
      ruleWithPendingGuardedRolloutConfig._id
        ? ruleWithPendingGuardedRolloutConfig._id
        : featureFlagConfig.rules?.[indexWithConfig].ref,
    );
    const controlAndTestVariationIds = getControlAndTestVariationIds({
      ruleId: configurationKey,
      rule: ruleWithPendingGuardedRolloutConfig,
      originalConfig,
      variations,
    });
    pendingGuardedRollout = parseConfigToPendingGuardedRollout({
      pendingGuardedRollout: ruleWithPendingGuardedRolloutConfig.guardedRolloutConfig,
      configurationKey,
      variations,
      controlAndTestVariationIds,
    });
  } else if (schemaConfig.fallthrough?.guardedRolloutConfig) {
    const controlAndTestVariationIds = getControlAndTestVariationIds({
      ruleId: FALLTHROUGH_RULE_ID,
      rule: schemaConfig.fallthrough,
      originalConfig,
      variations,
    });
    pendingGuardedRollout = parseConfigToPendingGuardedRollout({
      pendingGuardedRollout: schemaConfig.fallthrough.guardedRolloutConfig,
      configurationKey: FALLTHROUGH_RULE_ID,
      variations,
      controlAndTestVariationIds,
    });
  }

  return {
    featureFlagConfig,
    pendingProgressiveRollout,
    pendingGuardedRollout,
  };
}

/** End: JSON <-> Config **/

/** Config <-> JSON **/
function translateVariation<T>(variations: Variation[], match: Match<T> & { required: true }): number;
function translateVariation<T>(variations: Variation[], match: Match<T>): number | undefined;
function translateVariation<T>(variations: Variation[], match: Match<T> & { required?: true }) {
  const { id, required } = match;
  let varIdx = -1;
  if (typeof id === 'string') {
    varIdx = variations.findIndex((variation) => variation._id === id);
  }
  if (typeof id === 'number') {
    varIdx = id;
  }

  const variation = variations[varIdx];
  if (required) {
    nullthrows(variation, 'Expected a valid variation');
  }

  return varIdx === -1 ? undefined : varIdx;
}

export function translateClauseToJson(clause: Clause) {
  const parsedOperator = z.nativeEnum(CLAUSE_OPERATORS).safeParse(clause.op);

  const op = parsedOperator.success ? parsedOperator.data : '';

  const clauseSchema = {
    _id: clause._id,
    contextKind: clause.contextKind,
    attribute: clause.attribute,
    op,
    negate: clause.negate,
    values: clause.values.filter(
      (value) => typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number',
    ),
  } as ClauseSchema;

  if (!clause._id) {
    delete clauseSchema._id;
  }

  return clauseSchema;
}

function translateRolloutToJson(rollout: Rollout) {
  const { bucketBy, contextKind, experimentAllocation, variations } = rollout;
  return {
    bucketBy,
    contextKind,
    experimentAllocation,
    variations,
  };
}

function translateRuleToJson(params: {
  rule: Rule;
  progressiveRolloutConfig?: ProgressiveRolloutSchema | undefined;
  guardedRolloutConfig?: GuardedRolloutSchema | undefined;
  variations: Variation[];
}): RuleSchema {
  const { rule, progressiveRolloutConfig, guardedRolloutConfig, variations } = params;

  const sharedSchema: Pick<RuleSchema, '_id' | 'clauses' | 'description' | 'disabled'> = {
    _id: rule._id,
    clauses: rule.clauses.map(translateClauseToJson),
    description: rule.description,
  };

  if (disabledRules()) {
    sharedSchema.disabled = rule.disabled;
  }

  if (!rule._id) {
    delete sharedSchema._id;
  }

  // You should not be allowed to edit a rule with an experiment allocation -- will update this later
  if (rule.rollout?.experimentAllocation) {
    return {
      ...sharedSchema,
      rollout: translateRolloutToJson(rule.rollout),
    };
  }

  if (progressiveRolloutConfig) {
    return {
      ...sharedSchema,
      progressiveRolloutConfig,
    };
  }

  if (guardedRolloutConfig) {
    return {
      ...sharedSchema,
      guardedRolloutConfig,
    };
  }

  if (rule.rollout?.variations && !rule.rollout.experimentAllocation) {
    return {
      ...sharedSchema,
      percentageRolloutConfig: translatePercentageRolloutToJson(rule.rollout, variations),
    };
  }

  return {
    ...sharedSchema,
    variation: translateVariation(variations, {
      id: rule.variation,
    }),
  };
}

function translateGuardedReleasePhaseConfigToJson(
  guardedReleasePhaseConfig: GuardedReleasePhaseConfig &
    ConfigVariationIds & { allowControlVariationSelection: boolean },
  variations: Variation[],
): GuardedRolloutSchema {
  const {
    allowControlVariationSelection,
    randomizationUnit = '',
    testVariationId,
    controlVariationId,
    stages = [],
    metrics = [],
  } = guardedReleasePhaseConfig;

  const schema: GuardedRolloutSchema = {
    stages,
    metrics: metrics.map(({ onRegression, regressionThreshold: _regressionThreshold, ...metric }) => ({
      ...metric,
      onRegression: {
        rollback: onRegression.rollback,
      },
    })),
    metricSources: guardedReleasePhaseConfig.metricSources,
    randomizationUnit,
  };

  if (allowControlVariationSelection) {
    schema.testVariation = translateVariation(variations, {
      id: testVariationId,
      required: true,
    });
    schema.controlVariation = translateVariation(variations, {
      id: controlVariationId,
      required: true,
    });
  }

  return schema;
}

function translateProgressiveRolloutToJson(params: {
  progressiveRollout: PendingProgressiveReleasePhase['configuration'];
  variations: Variation[];
  controlVariationId: string;
  testVariationId: string;
  allowControlVariationSelection: boolean;
}): ProgressiveRolloutSchema {
  const { progressiveRollout, variations, controlVariationId, testVariationId, allowControlVariationSelection } =
    params;
  const { steps, contextKind } = progressiveRollout;
  const schema: ProgressiveRolloutSchema = {
    contextKind,
    steps: steps.map(({ rolloutWeight, duration }) => ({
      rolloutWeight,
      duration,
    })),
  };

  if (allowControlVariationSelection) {
    schema.controlVariation = translateVariation(variations, {
      id: progressiveRollout.controlVariationId || controlVariationId,
      required: true,
    });
    schema.endVariation = translateVariation(variations, {
      id: progressiveRollout.endVariationId || testVariationId,
      required: true,
    });
  }

  return schema;
}

function translatePercentageRolloutToJson(
  rollout: NonNullable<Fallthrough['rollout']>,
  variations: Variation[],
): PercentageRolloutSchema {
  return {
    contextKind: rollout.contextKind || 'user',
    bucketBy: rollout.bucketBy || 'key',
    variations: rollout.variations.map(({ variation, ...variationConfig }) => ({
      ...variationConfig,
      variation: translateVariation(variations, {
        id: variation,
        required: true,
      }),
    })),
  };
}

function translateFallthroughToJson(params: {
  fallthrough: Fallthrough;
  progressiveRolloutConfig?: ProgressiveRolloutSchema | undefined;
  guardedRolloutConfig?: GuardedRolloutSchema | undefined;
  variations: Variation[];
}): FallthroughSchema {
  const { fallthrough, progressiveRolloutConfig, guardedRolloutConfig, variations } = params;
  // You should not be allowed to edit a fallthrough with experiment allocation -- will update this later
  if (fallthrough.rollout?.experimentAllocation) {
    return { rollout: translateRolloutToJson(fallthrough.rollout) };
  }
  if (guardedRolloutConfig) {
    return {
      guardedRolloutConfig,
    };
  }
  if (progressiveRolloutConfig) {
    return {
      progressiveRolloutConfig,
    };
  }
  if (fallthrough.rollout?.variations && !fallthrough.rollout.experimentAllocation) {
    return {
      percentageRolloutConfig: translatePercentageRolloutToJson(fallthrough.rollout, variations),
    };
  }

  return {
    variation: translateVariation(variations, {
      id: fallthrough.variation,
    }),
  };
}

function translatePrerequisitesToJson(prerequisite: Prerequisite): Prerequisite {
  const { key, variation } = prerequisite;
  return {
    key,
    variation,
  };
}

// Combine the two together in the json to prevent confusion
function translateTargetsToJson(params: {
  targets?: FeatureFlagConfig['targets'];
  contextTargets?: FeatureFlagConfig['contextTargets'];
  variations: Variation[];
}): IndividualTargetingSchema[] {
  const { targets, contextTargets, variations } = params;
  return [...(targets ?? []), ...(contextTargets ?? [])]
    .filter(({ values }) => values.length > 0)
    .map((target) => {
      const { variation: variationIndex, ...targetConfig } = target;

      return {
        ...targetConfig,
        variation: translateVariation(variations, {
          id: variationIndex,
          required: true,
        }),
      };
    });
}

export function translateFlagConfigToJson(params: {
  draftConfig: FeatureFlagConfig;
  originalConfig: FeatureFlagConfig;
  pendingGuardedRollout: PendingGuardedReleasePhase | undefined;
  pendingProgressiveRollout: PendingProgressiveReleasePhase | undefined;
  variations: Variation[];
}): FeatureFlagConfigSchema {
  const { draftConfig, originalConfig, pendingGuardedRollout, pendingProgressiveRollout, variations } = params;
  const rolloutConfig = pendingProgressiveRollout || pendingGuardedRollout;
  let fallthrough: FallthroughSchema | undefined;
  const allowControlVariationSelection = variations.length > 2;

  const rules =
    draftConfig.rules?.map((rule) => {
      const ruleId = idOrKey(rule);
      if (ruleId === rolloutConfig?.configurationKey) {
        const { controlVariationId, testVariationId } = getControlAndTestVariationIds({
          ruleId,
          rule,
          originalConfig,
          variations,
        });
        const progressiveRolloutConfig: ProgressiveRolloutSchema | undefined = pendingProgressiveRollout
          ? translateProgressiveRolloutToJson({
              progressiveRollout: pendingProgressiveRollout.configuration,
              controlVariationId,
              testVariationId,
              variations,
              allowControlVariationSelection,
            })
          : undefined;
        const guardedRolloutConfig: GuardedRolloutSchema | undefined = pendingGuardedRollout
          ? translateGuardedReleasePhaseConfigToJson(
              {
                ...pendingGuardedRollout.configuration,
                controlVariationId: pendingGuardedRollout.controlVariationId || controlVariationId,
                testVariationId: pendingGuardedRollout.testVariationId || testVariationId,
                allowControlVariationSelection,
              },
              variations,
            )
          : undefined;

        return translateRuleToJson({ rule, progressiveRolloutConfig, guardedRolloutConfig, variations });
      }
      return translateRuleToJson({ rule, variations });
    }) ?? [];

  if (draftConfig.fallthrough) {
    if (rolloutConfig?.configurationKey === FALLTHROUGH_RULE_ID) {
      const { controlVariationId, testVariationId } = getControlAndTestVariationIds({
        ruleId: FALLTHROUGH_RULE_ID,
        rule: nullthrows(draftConfig.fallthrough),
        originalConfig,
        variations,
      });
      const progressiveRolloutConfig: ProgressiveRolloutSchema | undefined = pendingProgressiveRollout
        ? translateProgressiveRolloutToJson({
            progressiveRollout: pendingProgressiveRollout.configuration,
            controlVariationId,
            testVariationId,
            variations,
            allowControlVariationSelection,
          })
        : undefined;
      const guardedRolloutConfig: GuardedRolloutSchema | undefined = pendingGuardedRollout
        ? translateGuardedReleasePhaseConfigToJson(
            {
              ...pendingGuardedRollout.configuration,
              controlVariationId: pendingGuardedRollout.controlVariationId || controlVariationId,
              testVariationId: pendingGuardedRollout.testVariationId || testVariationId,
              allowControlVariationSelection,
            },
            variations,
          )
        : undefined;
      fallthrough = translateFallthroughToJson({
        fallthrough: draftConfig.fallthrough,
        progressiveRolloutConfig,
        guardedRolloutConfig,
        variations,
      });
    } else {
      fallthrough = translateFallthroughToJson({ fallthrough: draftConfig.fallthrough, variations });
    }
  }

  return {
    offVariation: draftConfig.offVariation,
    on: draftConfig.on,
    prerequisites: draftConfig.prerequisites?.map(translatePrerequisitesToJson),
    contextTargets: translateTargetsToJson({
      targets: draftConfig.targets,
      contextTargets: draftConfig.contextTargets,
      variations,
    }),
    rules,
    fallthrough,
  };
}

/** End: Config <-> JSON **/

export function parsePath(path: Array<string | number>) {
  return path
    .map((slice, idx) => {
      if (typeof slice === 'number') {
        return `[${slice}]`;
      }
      const leadingPeriod = idx === 0 ? '' : '.';
      return `${leadingPeriod}${slice}`;
    })
    .join('');
}

export function getItemDescription(item: { name?: string; description?: string; value?: unknown; _id?: string }) {
  const humanReadableName = item.name || item.description || '';
  const detail = item.value ?? item._id;
  if (detail !== undefined) {
    try {
      return [humanReadableName, stringifyValue(detail ?? '')].filter((value) => value.length).join(' - ');
    } catch {
      // Do nothing
    }
  }

  return humanReadableName;
}
